import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaUsers,
  FaBoxOpen,
  FaUserShield,
  FaChalkboardTeacher,
  FaShoppingCart,
  FaChartLine,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaGift,
  FaUserPlus,
  FaFileAlt,
  FaCog,
  FaUserTie,
  FaUserFriends,
  FaLayerGroup,
  FaMoneyBillWave,
  FaFileContract,
  FaClipboardList,
  FaExclamationTriangle,
  FaHeadset,
  FaUserCog
} from 'react-icons/fa';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [showReportsSubmenu, setShowReportsSubmenu] = useState(false);
  const [showAnalysisSubmenu, setShowAnalysisSubmenu] = useState(false);
  const [showSupportSubmenu, setShowSupportSubmenu] = useState(false);

  // detect screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isCompanyDashboard = location.pathname.startsWith('/company-dashboard');

  const menuItems = [
    {
      name: 'Dashboard',
      path: isCompanyDashboard ? '/company-dashboard' : '/company-dashboard',
      icon: <FaTachometerAlt />,
    },
    {
      name: 'Customers',
      path: isCompanyDashboard ? '/company-dashboard/customers' : '/company-dashboard',
      icon: <FaUsers />,
    },
    {
      name: 'Orders',
      path: isCompanyDashboard ? '/company-dashboard/orders' : '/company-dashboard',
      icon: <FaShoppingCart />,
    },
    {
      name: 'Sales Dashboard',
      path: isCompanyDashboard ? '/company-dashboard/sales-dashboard' : '/company-dashboard',
      icon: <FaChartLine />,
    },
    {
      name: 'Employees',
      path: isCompanyDashboard ? '/company-dashboard/employees' : '/company-dashboard',
      icon: <FaUserTie />,
    },
    {
      name: 'Trainings',
      path: isCompanyDashboard ? '/company-dashboard/total-trainers' : '/company-dashboard',
      icon: <FaChalkboardTeacher />,
    },

    {
      name: 'Products',
      path: isCompanyDashboard ? '/company-dashboard/products' : '/company-dashboard',
      icon: <FaBoxOpen />,
    },
    {
      name: 'Offer Products',
      path: isCompanyDashboard ? '/company-dashboard/offer-products' : '/dashboard/offer-products',
      icon: <FaGift />,
    },
    {
      name: 'Admin Place Order',
      path: isCompanyDashboard ? '/company-dashboard/admin-place-order' : '/company-dashboard',
      icon: <FaShoppingCart />,
    },

    {
      name: 'Users',
      path: isCompanyDashboard ? '/company-dashboard/user-list' : '/company-dashboard',
      icon: <FaUserFriends />,
    },
    {
      name: 'Add Admin',
      path: isCompanyDashboard ? '/company-dashboard/add-admin' : '/company-dashboard',
      icon: <FaUserPlus />,
    },
    // {
    //   name: 'Add CA User',
    //   path: isCompanyDashboard ? '/company-dashboard/add-ca-user' : '/company-dashboard',
    //   icon: <FaUserCog />,
    // },
    {
      name: 'Levels',
      path: isCompanyDashboard ? '/company-dashboard/levels' : '/company-dashboard',
      icon: <FaLayerGroup />,
    },
    {
      name: 'Withdraw Requests',
      path: isCompanyDashboard ? '/company-dashboard/withdraw-requests' : '/company-dashboard',
      icon: <FaMoneyBillWave />,
    },
    {
      name: 'Document Verification',
      path: isCompanyDashboard ? '/company-dashboard/document-verification' : '/company-dashboard',
      icon: <FaFileContract />,
    },
    {
      name: 'Settings',
      path: isCompanyDashboard ? '/company-dashboard/settings' : '/company-dashboard',
      icon: <FaCog />,
    },
    {
      name: 'Register Training Users',
      path: isCompanyDashboard ? '/company-dashboard/register-training-users' : '/company-dashboard',
      icon: <FaClipboardList />,
    },
  ];

  // Reports submenu items
  const reportsSubmenu = [
    {
      name: 'Profit & Loss',
      path: isCompanyDashboard ? '/company-dashboard/reports' : '/company-dashboard',
    },
    {
      name: 'Withdraw Reports',
      path: isCompanyDashboard ? '/company-dashboard/withdraw-report' : '/company-dashboard',
    },
    {
      name: 'Order Cancel Reports',
      path: isCompanyDashboard ? '/company-dashboard/order-cancel-report' : '/company-dashboard',
    },
    {
      name: 'Training Users List',
      path: isCompanyDashboard ? '/company-dashboard/training-users-list' : '/company-dashboard',
    },
    {
      name: 'Training Payment Report',
      path: isCompanyDashboard ? '/company-dashboard/training-payment-report' : '/company-dashboard',
    },
    {
      name: 'Referral Tree',
      path: isCompanyDashboard ? '/company-dashboard/referral-tree' : '/company-dashboard',
    },
    {
      name: 'Commission Distribution',
      path: isCompanyDashboard ? '/company-dashboard/commission-distribution-report' : '/company-dashboard',
    },
    // {
    //   name: 'Inventory Report',
    //   path: isCompanyDashboard ? '/company-dashboard/inventory-report' : '/dashboard/inventory-report',
    // },
  ];
  // Report Analysis submenu items
  const analysisSubmenu = [
    {
      name: 'Profit & Loss',
      path: isCompanyDashboard ? '/company-dashboard/reports' : '/company-dashboard',
    },
    {
      name: 'Purchase Entry',
      path: isCompanyDashboard ? '/company-dashboard/purchase-entry' : '/company-dashboard',
    },
    {
      name: 'Payment Entry',
      path: isCompanyDashboard ? '/company-dashboard/payment-entry' : '/company-dashboard',
    },
    {
      name: 'Training Expenses',
      path: isCompanyDashboard ? '/company-dashboard/training-entry' : '/company-dashboard',
    },
    {
      name: 'Creditor Ledger',
      path: isCompanyDashboard ? '/company-dashboard/creditor-ledger' : '/company-dashboard',
    },
    {
      name: 'Debtor Ledger',
      path: isCompanyDashboard ? '/company-dashboard/debtor-ledger' : '/company-dashboard',
    },
    {
      name: 'Balance Sheet',
      path: isCompanyDashboard ? '/company-dashboard/balance-sheet' : '/company-dashboard',
    },
    {
      name: 'Fixed Asset Ledger',
      path: isCompanyDashboard ? '/company-dashboard/fixed-asset-ledger' : '/company-dashboard',
    },
    {
      name: 'Loan Account Ledger',
      path: isCompanyDashboard ? '/company-dashboard/loan-account-ledger' : '/company-dashboard',
    },
    {
      name: 'GST Payable Ledger',
      path: isCompanyDashboard ? '/company-dashboard/gst-payable-ledger' : '/company-dashboard',
    },
    {
      name: 'Stock Balance',
      path: isCompanyDashboard ? '/company-dashboard/inventory-report' : '/dashboard/inventory-report',
    },
  ];

  // Support submenu items
  const supportSubmenu = [
    {
      name: 'Order Not Placed Manager',
      path: isCompanyDashboard ? '/company-dashboard/order-not-placed-manager' : '/company-dashboard',
    },
  ];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <div className="mobile-header">
          <button className="mobile-toggle-btn" onClick={toggleSidebar}>
            {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
          <h1 className="mobile-title">Panchagiri</h1>
        </div>
      )}

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div className="mobile-overlay" onClick={toggleSidebar}></div>
      )}

      <div className={`dashboard-sidebar ${isOpen ? 'expanded' : 'collapsed'} ${isMobile ? 'mobile-sidebar' : ''}`}>
        <ul className="dashboard-menu">
          {menuItems.map((item) => (
            <li key={item.name} className="dashboard-menu-item">
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `dashboard-link ${isActive ? 'active' : ''}`
                }
              >
                <span className="dashboard-icon">{item.icon}</span>
                {isOpen && <span className="dashboard-label">{item.name}</span>}
              </NavLink>
            </li>
          ))}

          {/* Reports with submenu */}
          <li className="dashboard-menu-item">
            <div
              className={`dashboard-link reports-header ${showReportsSubmenu ? 'active' : ''}`}
              onClick={() => isOpen && setShowReportsSubmenu(!showReportsSubmenu)}
            >
              <span className="dashboard-icon"><FaChartLine /></span>
              {isOpen && (
                <>
                  <span className="dashboard-label">Reports</span>
                  <span className="submenu-toggle">
                    {showReportsSubmenu ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </>
              )}
            </div>

            {/* Submenu */}
            {isOpen && showReportsSubmenu && (
              <ul className="reports-submenu">
                {reportsSubmenu.map((subItem) => (
                  <li key={subItem.name} className="submenu-item">
                    <NavLink
                      to={subItem.path}
                      className={({ isActive }) =>
                        `dashboard-link submenu-link ${isActive ? 'active' : ''}`
                      }
                    >
                      <span className="dashboard-label">{subItem.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Report Analysis with submenu */}
          <li className="dashboard-menu-item">
            <div
              className={`dashboard-link reports-header ${showAnalysisSubmenu ? 'active' : ''}`}
              onClick={() => isOpen && setShowAnalysisSubmenu(!showAnalysisSubmenu)}
            >
              <span className="dashboard-icon"><FaFileAlt /></span>
              {isOpen && (
                <>
                  <span className="dashboard-label">Analysis Report</span>
                  <span className="submenu-toggle">
                    {showAnalysisSubmenu ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </>
              )}
            </div>

            {/* Submenu */}
            {isOpen && showAnalysisSubmenu && (
              <ul className="reports-submenu">
                {analysisSubmenu.map((subItem) => (
                  <li key={subItem.name} className="submenu-item">
                    <NavLink
                      to={subItem.path}
                      className={({ isActive }) =>
                        `dashboard-link submenu-link ${isActive ? 'active' : ''}`
                      }
                    >
                      <span className="dashboard-label">{subItem.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Support with submenu */}
          <li className="dashboard-menu-item">
            <div
              className={`dashboard-link reports-header ${showSupportSubmenu ? 'active' : ''}`}
              onClick={() => isOpen && setShowSupportSubmenu(!showSupportSubmenu)}
            >
              <span className="dashboard-icon"><FaHeadset /></span>
              {isOpen && (
                <>
                  <span className="dashboard-label">Support</span>
                  <span className="submenu-toggle">
                    {showSupportSubmenu ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </>
              )}
            </div>

            {/* Submenu */}
            {isOpen && showSupportSubmenu && (
              <ul className="reports-submenu">
                {supportSubmenu.map((subItem) => (
                  <li key={subItem.name} className="submenu-item">
                    <NavLink
                      to={subItem.path}
                      className={({ isActive }) =>
                        `dashboard-link submenu-link ${isActive ? 'active' : ''}`
                      }
                    >
                      <span className="dashboard-label">{subItem.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>

          {/* Logout */}
          <li className="dashboard-menu-item logout-item">
            <button onClick={handleLogout} className="dashboard-links logout-btn">
              <span className="dashboard-icon"><FaSignOutAlt /></span>
              {isOpen && <span className="dashboard-label">Logout</span>}
            </button>
          </li>
        </ul>

        <style jsx>{`
          .dashboard-menu {
            display: flex;
            flex-direction: column;
            height: 100%;
          }

          .reports-header {
            cursor: pointer;
            justify-content: space-between;
          }

          .submenu-toggle {
            margin-left: auto;
            font-size: 0.75rem;
            transition: transform 0.2s ease;
          }

          .reports-submenu {
            list-style: none;
            padding: 0;
            margin: 4px 0 8px 0;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 8px;
            overflow: hidden;
          }

          .submenu-item {
            margin: 0;
          }

          .submenu-link {
            padding: 10px 16px 10px 48px;
            font-size: 0.9rem;
          }

          .submenu-link:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          .submenu-link.active {
            background: rgba(99, 102, 241, 0.3);
            border-left: 3px solid #6366f1;
          }

          .logout-item {
            background-color: #ff0303ff;
            border-radius: 16px;
            margin-top: auto;
          }

          .logout-btn {
            width: 100%;
            text-align: left;
            background: none;
            border: none;
            color: inherit;
            cursor: pointer;
          }

          /* Mobile overlay */
          .mobile-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            z-index: 10;
          }

          .mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            background: #111;
            color: #fff;
          }

          .mobile-toggle-btn {
            background: none;
            border: none;
            color: white;
            font-size: 1.5rem;
            cursor: pointer;
          }
        `}</style>
      </div>
    </>
  );
};

export default Sidebar;
